'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface ArticleImageEntry {
	src: string;
	alt: string;
	caption?: string;
}

const ArticleImagesContext = createContext<ArticleImageEntry[]>([]);

/**
 * All images of the current article, in document order. Empty outside a
 * provider (for example an isolated preview) - ArticleImage then falls back to a
 * single-image lightbox.
 */
export const useArticleImages = (): ArticleImageEntry[] => useContext(ArticleImagesContext);

const IMAGE_RE = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;
const VIDEO_RE = /\.(mp4|webm|mov|ogv|ogg)(?:[?#]|$)/i;

/**
 * Extracts every `![alt](url "caption")` from the raw markdown body so each
 * rendered ArticleImage can open the lightbox with the full set and navigate
 * between images with the arrow keys. Video URLs are skipped - they render as
 * an inline ArticleVideo player and must not become navigable lightbox images.
 */
export const ArticleImagesProvider = ({
	body,
	children,
}: {
	body: string;
	children: ReactNode;
}): React.JSX.Element => {
	const images = useMemo(
		() =>
			Array.from(body.matchAll(IMAGE_RE), (match) => ({
				alt: match[1],
				src: match[2],
				caption: match[3] || undefined,
			})).filter((image) => !VIDEO_RE.test(image.src)),
		[body]
	);
	return <ArticleImagesContext.Provider value={images}>{children}</ArticleImagesContext.Provider>;
};
